import React, { useState } from "react";
import { Dispatch, SetStateAction } from "react";
import { Button, Typography, Card, Dialog }  from "@material-tailwind/react";
import { FormProvider, useForm } from 'react-hook-form'

import { Vehicle } from "../../../../redux/vehicles/actions";
import { Field, RenderFormField } from "../../../../components/DetailsFormField";
import { StyledScrollContainer, StyledFixedElement } from "../../../../components/StyledScrollContainer";

const PLAN_TYPES = [
    { n: "Starter", f: "starter", v: "starter", type: "string" },
    { n: "Pro", f: "pro", v: "pro", type: "string" },
    { n: "Enterprise", f: "enterprise", v: "enterprise", type: "string" },
]

const PRICING_TYPES = [
    { n: "Dynamic", f: "dynamic", v: "dynamic", type: "string" },
    { n: "Fixed", f: "fixed", v: "fixed", type: "string" },
]

const WARRANTY_TYPES = [
    { n: "Basic", f: "basic", v: "basic", type: "string" },
    { n: "Extended", f: "extended", v: "extended", type: "string" },
    { n: "None", f: "none", v: "none", type: "string" },
]

interface Section {
    title: string;
    subTitle: string;
    type: string;
    fields?: Field[];
    options?: any[];
}

const SECTIONS: Section[] = [
    {
        title: "Add Vehicle",
        subTitle: "Add vehicles fast with specs, photos, and pricing updates.",
        type: "render",
        fields: [
            { n: "VIN", f: "vin", type: "text", editable: true, readonly: true, required: true },
            { n: "Make", f: "make", type: "text", editable: true, required: true },
            { n: "Model", f: "model", type: "text", editable: true, required: true },
            { n: "Year", f: "year", type: "number", editable: true, required: true },
            { n: "Trim", f: "trim", type: "text", editable: true, required: true },
            { n: "Miles", f: "miles", type: "number", placeholder: "Enter Miles", editable: true, required: true },
            { n: "Engine", f: "engine_type", type: "text", placeholder: "Enter Engine", editable: true, required: true },
            { n: "Body Style", f: "body_style", type: "select", options: [], editable: true, required: true },
            { n: "Drive Type", f: "drive_type", type: "select", options: [], editable: true },
            { n: "Color Interior", f: "color_interior", type: "text", placeholder: "Enter Color Interior", editable: true },
            { n: "Color Exterior", f: "color_exterior", type: "text", placeholder: "Enter Color Exterior", editable: true },
        ]
    },
    {
        title: "Plan & Pricing",
        subTitle: "Add vehicles fast with specs, photos, and pricing updates.",
        type: "render",
        fields: [
            { n: "Plan Type", f: "plan_type", type: "select", options: PLAN_TYPES, editable: true, required: true },
            { n: "Pricing Type", f: "pricing_type", type: "select", options: PRICING_TYPES, editable: true, required: true },
            { n: "Sale Price", f: "estimated_sale_price", type: "money", editable: true, required: true },
        ]
    },
    {
        title: 'Warranty',
        subTitle: 'Coverage you can trust. Review details for every vehicle with ease.',
        type: "render",
        fields: [
            { n: "Warranty Type", f: "warranty_type", type: "select", options: WARRANTY_TYPES, editable: true, required: true },
            { n: "Details", f: "warranty_details", type: "text", editable: true, readonly: false },
            { n: "Coverage", f: "warranty_coverage", type: "text", editable: true, readonly: false },
        ]
    },
    {
        title: 'Features',
        subTitle: 'Here’s a quick overview of its standout features:',
        type: "features",
        options: [],
    },
    {
        title: 'Photos',
        subTitle: 'View all the image from the previous year',
        type: "photos",
        options: [],
    },
    {
        title: '360° view',
        subTitle: 'Upload in the order requested to create a virtual 360º tour.',
        type: "photos_360",
        options: [],
    },
    {
        title: 'Interior Photos',
        subTitle: 'View all the image from the previous year',
        type: "photos_interior",
        options: [],
    }
]

interface DialogAddVehicleProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    onSubmit: (vehicle: Vehicle) => void;
}

export const DialogAddVehicleSeller: React.FC<DialogAddVehicleProps> = ({
    isOpen,
    setIsOpen,
    onSubmit,
}) => {
    const [formData, setFormData] = useState<Record<string, any>>({});
    const methods = useForm();

    const onFormSubmit = methods.handleSubmit(data => {
        onSubmit(data as Vehicle);
    });

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen} size="xl">
            <Dialog.Overlay>
                <Dialog.Content className="bg-[#F8F9FA] flex h-screen -overflow-hidden">
                    <StyledScrollContainer
                        className="flex flex0lastScrollTop.current -overflow-y-auto flex-col w-full -max-w-[1400px] -mx-auto"
                        hideFixedElementOnScroll={false}
                    >
                        <StyledFixedElement className="z-10 ns-fixed-element bg-[#F8F9FA] isolate w-full -md:w-[calc(100vw-300px-10rem)]">
                            <div className="flex gap-3 justify-end p-6">
                                <Button
                                    variant="outline"
                                    color="secondary"
                                    onClick={() => setIsOpen(false)}
                                    className="mr-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="solid"
                                    color="info"
                                    onClick={onFormSubmit}
                                >
                                    Save and Sell
                                </Button>
                            </div>
                        </StyledFixedElement>
                        <FormProvider {...methods}>
                            <form
                                className="flex flex-col p-2 md:p-6"
                                onSubmit={(e) => e.preventDefault()}
                                noValidate
                                autoComplete="off"
                            >
                                {SECTIONS.map((section, index) => (
                                    <Card key={index} className="p-4 mb-4">
                                        <Typography className="text-[16px] text-[#1F2937] font-bold">
                                            {section.title}
                                        </Typography>
                                        <Typography className="mt-2 text-[14px] text-[#64748B] font-[450]">
                                            {section.subTitle}
                                        </Typography>

                                        <div
                                            className={`mt-6 grid grid-cols-1 md:grid-cols-3 gap-4`}
                                        >
                                            {section.fields?.map((field, fieldIndex) => (
                                                <div key={fieldIndex}>
                                                    <RenderFormField
                                                        field={field}
                                                        isEdit={true}
                                                        object={formData}
                                                        setNewObject={setFormData}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </Card>
                                ))}
                            </form>
                        </FormProvider>
                    </StyledScrollContainer>
                </Dialog.Content>
            </Dialog.Overlay>
        </Dialog>
    );
};